import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import BookingPreview from "../ui/BookingPreview";

/**
 * About Component
 * Intro section: product preview on the left, value props on the right.
 *
 * `#about` and `.icon-box` are load-bearing selectors for the e2e suite.
 */
const About = ({ title = null, description = null, features = [] }) => {
  const { t } = useTranslation();

  const defaultFeatures = [
    {
      icon: "bx-calendar-check",
      title: t("About.features.onlineBooking.title"),
      description: t("About.features.onlineBooking.description"),
    },
    {
      icon: "bx-user-circle",
      title: t("About.features.resourceManagement.title"),
      description: t("About.features.resourceManagement.description"),
    },
    {
      icon: "bx-bar-chart-alt-2",
      title: t("About.features.businessInsights.title"),
      description: t("About.features.businessInsights.description"),
    },
    {
      icon: "bx-bell",
      title: t("About.features.automatedReminders.title"),
      description: t("About.features.automatedReminders.description"),
    },
  ];

  const displayTitle = title || t("About.title");
  const displayDescription = description || t("About.description");
  const featuresToDisplay = features.length > 0 ? features : defaultFeatures;

  return (
    <section
      id="about"
      className="about relative overflow-hidden bg-ink-50 pt-28 pb-16 sm:pt-32 sm:pb-24"
    >
      {/* Soft brand wash sitting behind the fixed header. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-brand-50 to-transparent"
      />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: product preview — replaces the old stock photo. */}
          <div className="image order-2 lg:order-1">
            <BookingPreview />
          </div>

          {/* Right: copy + value props */}
          <div className="content order-1 lg:order-2">
            <h3 className="text-fluid-3xl font-bold leading-tight tracking-tight text-ink-900">
              {displayTitle}
            </h3>
            <p className="mt-5 text-fluid-base leading-relaxed text-ink-500">
              {displayDescription}
            </p>

            <div className="mt-10 grid gap-6 sm:grid-cols-2">
              {featuresToDisplay.map((feature, index) => (
                <div key={index} className="icon-box">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
                      <i
                        className={`bx ${feature.icon} text-xl`}
                        aria-hidden="true"
                      />
                    </span>
                    <h4 className="text-base font-semibold text-ink-900">
                      {feature.title}
                    </h4>
                  </div>
                  <p className="mt-2 text-fluid-sm leading-relaxed text-ink-500">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#pricing"
                className="inline-flex items-center justify-center rounded-full bg-brand-600 px-7 py-3 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {t("About.cta.primary", "See pricing")}
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center rounded-full border border-ink-300 bg-white px-7 py-3 text-sm font-semibold text-ink-700 transition hover:border-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {t("About.cta.secondary", "Explore features")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

About.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  ),
};

export default About;
